/**
 * LES COORDONNÉES FIGÉES D'UNE FACTURE.
 *
 * Le défaut corrigé ici : le PDF relisait la fiche client VIVANTE. Corriger une
 * adresse aujourd'hui réécrivait le document d'une facture émise il y a six
 * mois — et l'application déclarait pourtant l'inverse, avec un champ
 * « clientSnapshot » commenté « immuabilité » qui recevait la relation vivante
 * et que personne ne lisait.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * QUAND CELA GÈLE. Une facture « Brouillon » et non verrouillée RECALCULE son
 * snapshot à chaque enregistrement : tant qu'on prépare le document, corriger
 * une coquille dans la fiche client doit se voir. Au premier passage en
 * « Envoyée », « Téléchargée », « Payée » — ou au verrouillage — « snapshotGeleLe »
 * est posé, et plus rien ne le réécrit.
 *
 * C'est la règle comptable ordinaire : un document ÉMIS est immuable, un
 * brouillon ne l'est pas.
 *
 * CE QUI EST FIGÉ. Les coordonnées : identité, adresse, identifiants fiscaux,
 * coordonnées bancaires. Prêtes à l'affichage — romanisées, pays nommé — avec
 * l'écriture d'origine à côté pour la seconde ligne du bloc client.
 *
 * CE QUI EST FIGÉ AUSSI, et c'est une correction du 9 septembre 2026 : les
 * TEXTES CONTRACTUELS imprimés sur le document — CGV, mentions légales,
 * mentions de retard, conditions de paiement. J'avais d'abord voulu les laisser
 * vivants, en les rangeant du côté de la présentation. C'était une erreur : ce
 * sont des engagements portés par le document, pas une mise en forme. Les CGV
 * qui figuraient sur une facture émise sont celles qui engagent, et les corriger
 * six mois plus tard réécrirait un contrat déjà conclu.
 *
 * CE QUI RESTE VIVANT : le logo, les couleurs, la taille du logo, la
 * disposition. La mise en page peut évoluer ; ce qui est ÉCRIT sur le document,
 * non.
 */

import type { Client, Societe } from "@/types";
import { getClientDisplayName, getClientOriginalName, getClientAddressLines, getClientOriginalAddress } from "./client-utils";
import { translittererPourPdf, detecterScript } from "./translitteration";
import { nomDuPays } from "./pays";

/** La version de forme. Un changement se reconnaît, il ne se devine pas. */
export const VERSION_SNAPSHOT = 1;

export interface SnapshotClient {
    v: number;
    /** Le nom AFFICHÉ : romanisé si nécessaire, jamais vide. */
    nom: string;
    /** L'écriture d'origine, si elle dit autre chose. */
    nomOriginal?: string;
    /** Les lignes d'adresse, romanisées, pays NOMMÉ, vides écartées. */
    adresse: string[];
    /** Les mêmes dans l'écriture d'origine, si elle dit autre chose. */
    adresseOriginale?: string[];
    /** « latin », « cyrillique », « arabe »… — le PDF en a besoin pour choisir
     *  sa police et le sens de lecture de la seconde ligne. */
    script?: string;
    email?: string;
    telephone?: string;
    siret?: string;
    tvaIntra?: string;
}

export interface SnapshotSociete {
    v: number;
    nom: string;
    formeJuridique?: string;
    adresse: string[];
    siret?: string;
    tvaIntra?: string;
    rcs?: string;
    capitalSocial?: string;
    telephone?: string;
    email?: string;
    banque?: string;
    iban?: string;
    bic?: string;

    /** LES TEXTES CONTRACTUELS, tels qu'ils figuraient sur le document émis.
     *  Voir l'en-tête : ce sont des engagements, pas de la mise en forme. */
    mentionsLegales?: string;
    mentionsRetard?: string;
    cgv?: string;
    /** Le repli des conditions de paiement, quand la facture n'en porte pas
     *  elle-même. Sans lui, changer les conditions par défaut réécrirait la
     *  ligne « Conditions : … » de toutes les factures passées. */
    conditionsParDefaut?: string;
}

/** Les statuts qui ferment un document. Au premier d'entre eux, le snapshot
 *  est figé — même si la facture redevient « Brouillon » ensuite, ce qui ne
 *  devrait pas arriver mais que rien n'interdit. */
const STATUTS_EMIS = new Set(["Envoyée", "Téléchargée", "Payée", "Retard", "Annulée", "Archivée"]);

/**
 * Le document est-il encore modifiable, donc son snapshot encore recalculable ?
 *
 * Le verrou compte autant que le statut : « isLocked » est posé automatiquement
 * dès qu'une facture est payée ou archivée, et il rend déjà l'éditeur en
 * lecture seule. Une facture verrouillée dont on rafraîchirait les coordonnées
 * serait un document figé à l'écran et mouvant dans son PDF.
 */
export function estEncoreBrouillon(facture: {
    statut?: string | null;
    isLocked?: boolean | null;
    snapshotGeleLe?: Date | string | null;
}): boolean {
    if (facture.snapshotGeleLe) return false;
    if (facture.isLocked) return false;
    return !STATUTS_EMIS.has(String(facture.statut ?? "Brouillon"));
}

/**
 * Construit le snapshot d'un client.
 *
 * @param paysBrut rend le PAYS tel quel au lieu de le nommer. N'a qu'un usage :
 *        la reprise des factures DÉJÀ ENVOYÉES, dont l'archive doit reproduire
 *        ce que le destinataire a reçu — « FR » et non « France ». Aucun
 *        parcours courant ne s'en sert.
 */
export function construireSnapshotClient(
    client: Client | null | undefined,
    options?: { paysBrut?: boolean }
): SnapshotClient {
    if (!client) return { v: VERSION_SNAPSHOT, nom: "", adresse: [] };

    const lignes = options?.paysBrut
        ? lignesAvecPaysBrut(client)
        : getClientAddressLines(client);

    const original = getClientOriginalName(client);
    const adresseOriginale = getClientOriginalAddress(client);

    return {
        v: VERSION_SNAPSHOT,
        nom: getClientDisplayName(client),
        ...(original ? { nomOriginal: original } : {}),
        adresse: lignes,
        ...(adresseOriginale.length ? { adresseOriginale } : {}),
        ...(client.scriptOriginal ? { script: client.scriptOriginal } : {}),
        ...(client.email ? { email: client.email } : {}),
        ...(client.telephone ? { telephone: client.telephone } : {}),
        ...(client.siret ? { siret: client.siret } : {}),
        ...(client.tvaIntra ? { tvaIntra: client.tvaIntra } : {}),
    };
}

/** Les mêmes lignes, mais le pays laissé dans sa forme d'origine. */
function lignesAvecPaysBrut(client: Client): string[] {
    const lignes = getClientAddressLines(client);
    if (!lignes.length) return lignes;

    const nomme = nomDuPays(client.pays);
    const brut = (client.pays ?? "").trim();
    if (!brut || nomme === brut) return lignes;

    // Le pays est toujours la dernière ligne, et seulement s'il y a une adresse.
    return lignes.map((l, i) => (i === lignes.length - 1 && l === nomme ? brut : l));
}

/**
 * Construit le snapshot de la société émettrice.
 *
 * Elle n'a pas de colonnes latines — aucune société rencontrée n'en a besoin —
 * donc la romanisation se calcule ici, à l'écriture. C'est le seul endroit où
 * elle a encore lieu à la volée, et le résultat est figé comme le reste.
 */
export function construireSnapshotSociete(societe: Societe | null | undefined): SnapshotSociete {
    if (!societe) return { v: VERSION_SNAPSHOT, nom: "", adresse: [] };

    const l = (v?: string | null) => translittererPourPdf(v ?? "");

    const adresse = [
        l(societe.adresse),
        [societe.codePostal?.trim(), l(societe.ville)].filter(Boolean).join(" "),
        nomDuPays(societe.pays) || "France",
    ].filter(Boolean);

    return {
        v: VERSION_SNAPSHOT,
        nom: l(societe.nom),
        ...(societe.formeJuridique ? { formeJuridique: l(societe.formeJuridique) } : {}),
        adresse,
        ...(societe.siret ? { siret: societe.siret } : {}),
        ...(societe.tvaIntra ? { tvaIntra: societe.tvaIntra } : {}),
        ...(societe.rcs ? { rcs: societe.rcs } : {}),
        ...(societe.capitalSocial ? { capitalSocial: societe.capitalSocial } : {}),
        ...(societe.telephone ? { telephone: societe.telephone } : {}),
        ...(societe.email ? { email: societe.email } : {}),
        ...(societe.banque ? { banque: l(societe.banque) } : {}),
        ...(societe.iban ? { iban: societe.iban } : {}),
        ...(societe.bic ? { bic: societe.bic } : {}),
        ...(societe.mentionsLegales ? { mentionsLegales: l(societe.mentionsLegales) } : {}),
        ...(societe.mentionsRetard ? { mentionsRetard: l(societe.mentionsRetard) } : {}),
        ...(societe.cgv ? { cgv: l(societe.cgv) } : {}),
        ...(societe.defaultConditions ? { conditionsParDefaut: societe.defaultConditions } : {}),
    };
}

/**
 * Ce qu'il faut écrire sur une facture, snapshot compris.
 *
 * Rend un objet VIDE lorsque la facture est déjà figée : l'appelant peut donc
 * l'étaler sans condition dans son « data », et aucun chemin d'écriture ne peut
 * réécrire un snapshot par inadvertance. C'est le point : la protection est
 * dans la fonction, pas dans la discipline de chaque appelant.
 */
export function colonnesSnapshot(
    facture: { statut?: string | null; isLocked?: boolean | null; snapshotGeleLe?: Date | string | null },
    client: Client | null | undefined,
    societe: Societe | null | undefined,
    options?: { paysBrut?: boolean; gelerMaintenant?: boolean }
): { snapshotClient?: string; snapshotSociete?: string; snapshotGeleLe?: Date } {
    if (!estEncoreBrouillon(facture)) return {};

    const gele =
        options?.gelerMaintenant ?? STATUTS_EMIS.has(String(facture.statut ?? "Brouillon"));

    return {
        snapshotClient: JSON.stringify(construireSnapshotClient(client, options)),
        snapshotSociete: JSON.stringify(construireSnapshotSociete(societe)),
        ...(gele || facture.isLocked ? { snapshotGeleLe: new Date() } : {}),
    };
}

/**
 * LES CHAMPS QUI FONT L'IMMUABILITÉ, À REPRENDRE TELS QUELS.
 *
 * Une quinzaine d'écrans RECONSTRUISENT le document avant de l'envoyer au
 * générateur — l'éditeur le rebâtit depuis l'état du formulaire, le mobile
 * depuis ses champs locaux. Ces objets-là ne portent que ce qu'on y met, et
 * personne n'y mettait le snapshot : le générateur recevait « undefined » et
 * retombait sur les fiches VIVANTES.
 *
 * C'est le même piège que dans les mappages Prisma, déplacé d'un cran. Vu le
 * 9 septembre 2026 en préproduction, sur une facture PAYÉE et gelée dont
 * l'aperçu affichait la nouvelle raison sociale, la nouvelle adresse, le
 * nouvel IBAN et les mentions réécrites.
 *
 * D'où cette fonction plutôt que quatre lignes recopiées : elle se cherche,
 * elle se teste, et un garde-fou vérifie qu'aucun document reconstruit ne part
 * au PDF ou au courriel sans elle.
 */
export function champsImmuables(source: unknown): {
    statut?: string;
    isLocked: boolean;
    snapshotClient: string | null;
    snapshotSociete: string | null;
    snapshotGeleLe: Date | string | null;
} {
    const s = (source ?? {}) as Record<string, unknown>;
    return {
        // « statut » n'est repris que si l'appelant n'en pose pas lui-même :
        // il l'écrase par le sien, posé APRÈS l'étalement.
        ...(s.statut ? { statut: String(s.statut) } : {}),
        isLocked: !!s.isLocked,
        snapshotClient: (s.snapshotClient as string) ?? null,
        snapshotSociete: (s.snapshotSociete as string) ?? null,
        snapshotGeleLe: (s.snapshotGeleLe as Date) ?? null,
    };
}

/**
 * Relit un snapshot. Rend « null » s'il est absent ou illisible — l'appelant
 * retombe alors sur la fiche vivante, ce qui est le comportement d'avant ce
 * chantier et reste préférable à un document vide.
 */
export function lireSnapshotClient(json?: string | null): SnapshotClient | null {
    return lire<SnapshotClient>(json);
}

export function lireSnapshotSociete(json?: string | null): SnapshotSociete | null {
    return lire<SnapshotSociete>(json);
}

function lire<T>(json?: string | null): T | null {
    if (!json) return null;
    try {
        const objet = JSON.parse(json);
        if (!objet || typeof objet !== "object") return null;
        return objet as T;
    } catch {
        // « warn » et non « log » : « removeConsole » ne laisse passer que
        // « error » et « warn » en production.
        console.warn("[Facture] snapshot illisible, retour à la fiche vivante");
        return null;
    }
}

/** Le script d'origine d'un snapshot, pour que le PDF choisisse sa police. */
export function scriptDuSnapshot(snapshot: SnapshotClient | null): string {
    if (!snapshot) return "latin";
    if (snapshot.script) return snapshot.script;
    // Snapshot d'une version antérieure, sans le champ : on le déduit.
    return detecterScript(snapshot.nomOriginal ?? snapshot.nom);
}

/**
 * COMBLER L'ADRESSE MANQUANTE D'UN SNAPSHOT — et rien d'autre.
 *
 * Amine, 19 septembre 2026 : « et si j'en ai d'autres dans le futur ? il va
 * m'en manquer 3 ». La reprise du jour rattrape les factures figées sans
 * adresse ; celle-ci rend le geste PERMANENT : quand une fiche client est
 * enregistrée, ses factures figées dont le bloc client n'a pas d'adresse
 * reçoivent la sienne.
 *
 * La règle tient en une phrase : on ne comble qu'une ABSENCE. Un snapshot
 * qui porte déjà des lignes d'adresse est le document reçu par le client ;
 * il ne se réécrit pas. Rend `null` quand il n'y a rien à faire — snapshot
 * illisible, adresse déjà présente, ou fiche toujours sans adresse.
 */
export function snapshotClientAvecAdresseComblee(
    json: string | null | undefined,
    client: Client | null | undefined
): string | null {
    const snapshot = lireSnapshotClient(json);
    if (!snapshot) return null;
    if (Array.isArray(snapshot.adresse) && snapshot.adresse.length > 0) return null;

    const lignes = getClientAddressLines(client);
    if (lignes.length === 0) return null;

    const adresseOriginale = getClientOriginalAddress(client);
    return JSON.stringify({
        ...snapshot,
        adresse: lignes,
        ...(adresseOriginale.length ? { adresseOriginale } : {}),
    });
}
